import AddIcon from "@mui/icons-material/AddRounded";
import RemoveIcon from "@mui/icons-material/RemoveRounded";
import type { Theme } from "@mui/material";
import { Box, Collapse, Stack, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import ExpandCollapseButton from "@entrilia/shared/components/ExpandCollapseButton";
import RoundedTable from "@entrilia/shared/components/RoundedTable";
import StatusChip from "@entrilia/shared/components/StatusChip";
import UserAvatar from "@entrilia/shared/components/UserAvatar";
import CollapseIcon from "@entrilia/shared/icons/CollapseIcon";
import ExpandIcon from "@entrilia/shared/icons/ExpandIcon";
import type { MemberChangeAction } from "../../../../../api/types/membersChangeRequestTypes";
import PortalRoleChip from "../../../../common/PortalRoleChip";
import { getChangesCountChipLabel } from "../contactChangeLabels";
import type { MemberCategoryChangeType, MemberChange } from "../membersChangeRequestHelper";
import { getChangesCount } from "../membersChangeRequestHelper";

interface Props {
  change: MemberChange;
  expanded: boolean;
  onToggleExpanded: () => void;
}

const getChangeChipLabel = (change: MemberChange): string => {
  switch (change.action) {
    case "CreateNew":
      return "Adding";
    case "Delete":
      return "Removing";
    default:
      return getChangesCountChipLabel(getChangesCount(change));
  }
};

const getChangeChipColor = (t: Theme, action: MemberChangeAction): string => {
  switch (action) {
    case "UpdateExisting":
      return t.palette.warning.main;
    case "Delete":
      return t.palette.error.main;
    default:
      return t.palette.info.main;
  }
};

const categoryChipIcons: Record<MemberCategoryChangeType, React.ReactElement> = {
  Added: <AddIcon />,
  Removed: <RemoveIcon />,
};

const MemberChangeRow = ({ change, expanded, onToggleExpanded }: Props) => {
  const isRemoval = change.action === "Delete";

  return (
    <>
      <TableRow sx={{ "& td": { borderBottom: 0 } }}>
        <TableCell sx={{ width: 48, px: 0.75 }}>
          <ExpandCollapseButton
            expanded={expanded}
            onToggle={onToggleExpanded}
            ExpandIcon={ExpandIcon}
            CollapseIcon={CollapseIcon}
            sx={{ my: -1 }}
            aria-label={expanded ? "Collapse member change details" : "Expand member change details"}
          />
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <UserAvatar size="medium" userName={change.name} />
            <Stack spacing={0.25} sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Typography variant="subtitle2">{change.name}</Typography>
                <StatusChip label={getChangeChipLabel(change)} color={(t) => getChangeChipColor(t, change.action)} />
              </Stack>
              <Typography variant="caption" color="textSecondary">
                {change.email}
              </Typography>
            </Stack>
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={2} sx={{ "&.MuiTableCell-root.MuiTableCell-body": { p: 0 } }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ borderTop: "1px solid", borderColor: "divider", pl: 8, pr: 2, py: 1.375 }}>
              <RoundedTable withoutColumnBorders>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "40%" }}>Funds</TableCell>
                    <TableCell sx={(t) => ({ color: isRemoval ? t.palette.error.light : t.palette.success.light })}>
                      {isRemoval ? "Removed" : "Requested"}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {change.fundChanges.map((fundChange, index) => (
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    <TableRow key={fundChange.fundNames.join("|") || index}>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Stack spacing={0.5}>
                          {fundChange.fundNames.map((fundName) => (
                            <Typography key={fundName}>{fundName}</Typography>
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Stack spacing={1.5}>
                          {(change.action !== "UpdateExisting" || fundChange.roleChanged) && (
                            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                              <Typography color="textSecondary">Role:</Typography>
                              <PortalRoleChip role={fundChange.role ?? "None"} />
                            </Stack>
                          )}
                          {(change.action !== "UpdateExisting" || fundChange.categoryChanges.length > 0) && (
                            <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
                              <Typography color="textSecondary" sx={{ pt: 0.25 }}>
                                Documents:
                              </Typography>
                              {fundChange.categoryChanges.length > 0 ? (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                  {fundChange.categoryChanges.map((categoryChange) => (
                                    <StatusChip
                                      key={`${categoryChange.changeType}-${categoryChange.name}`}
                                      icon={categoryChipIcons[categoryChange.changeType]}
                                      label={categoryChange.name}
                                      color={(t) =>
                                        categoryChange.changeType === "Removed"
                                          ? t.palette.error.main
                                          : t.palette.success.main
                                      }
                                    />
                                  ))}
                                </Box>
                              ) : (
                                <Typography color="textSecondary" sx={{ pt: 0.25 }}>
                                  None
                                </Typography>
                              )}
                            </Stack>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </RoundedTable>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default MemberChangeRow;
